/**
 * Wishlist Manager - LOCALSTORAGE MODE
 * Simple localStorage-based wishlist system (requires login)
 * 2025 SEMU Theme
 * Version: 4.0 - Clean & Simple
 */

class WishlistManager {
  constructor() {
    // Fixed storage key for all users (no login required)
    this.storageKey = 'semu_wishlist';
    this.items = [];
    this.init();
  }

  init() {
    // Load from localStorage
    this.load();

    this.attachEventListeners();
    this.updateAllButtons();
    this.updateCounters();
  }

  // Check if customer is logged in (not required anymore)
  isLoggedIn() {
    return true; // Allow guest users
  }

  // Load wishlist from localStorage
  load() {
    try {
      const data = localStorage.getItem(this.storageKey);

      if (data) {
        const parsed = JSON.parse(data);

        // Handle both old format (array) and new format (object with items)
        if (Array.isArray(parsed)) {
          this.items = parsed;
          // Save in new format
          this.save();
        } else {
          this.items = parsed.items || [];
        }
      } else {
        this.items = [];
      }
    } catch (error) {
      console.error('[Wishlist] Error loading:', error);
      this.items = [];
    }
  }

  // Save wishlist to localStorage
  save() {
    try {
      const data = {
        items: this.items,
        updated: new Date().toISOString(),
      };

      localStorage.setItem(this.storageKey, JSON.stringify(data));
      this.dispatchEvent('wishlist:updated', { items: this.items, count: this.items.length });
      return true;
    } catch (error) {
      console.error('[Wishlist] Error saving:', error);
      this.showToast('Error saving wishlist', 'error');
      return false;
    }
  }

  // Check if product is in wishlist
  has(productId) {
    return this.items.some((item) => item.id === String(productId));
  }

  // Add product to wishlist
  add(productData) {
    // No login required - guests can use wishlist

    const productId = String(productData.id || productData.productId);

    if (this.has(productId)) {
      return false;
    }

    const wishlistItem = {
      id: productId,
      handle: productData.handle || '',
      title: productData.title || '',
      vendor: productData.vendor || '',
      price: productData.price || 0,
      compareAtPrice: productData.compareAtPrice || null,
      image: productData.image || '',
      url: productData.url || `/products/${productData.handle}`,
      available: productData.available !== false,
      addedAt: Date.now(),
    };

    this.items.unshift(wishlistItem);
    this.save();

    this.updateAllButtons();
    this.updateCounters();

    this.dispatchEvent('wishlist:added', { item: wishlistItem });
    this.showToast('Added to wishlist', 'success');
    return true;
  }

  // Remove product from wishlist
  remove(productId) {
    // No login required

    const productIdStr = String(productId);
    const index = this.items.findIndex((item) => item.id === productIdStr);

    if (index === -1) {
      return false;
    }

    const removed = this.items.splice(index, 1)[0];
    this.save();

    this.updateAllButtons();
    this.updateCounters();

    this.dispatchEvent('wishlist:removed', { item: removed });
    this.showToast('Removed from wishlist', 'success');
    return true;
  }

  // Toggle product in wishlist
  toggle(productData) {
    const productId = String(productData.id || productData.productId);

    if (this.has(productId)) {
      return this.remove(productId);
    } else {
      return this.add(productData);
    }
  }

  // Get all wishlist items
  getAll() {
    return [...this.items];
  }

  // Get wishlist count
  count() {
    return this.items.length;
  }

  // Clear entire wishlist
  clear() {
    // No login required

    this.items = [];
    this.save();

    this.updateAllButtons();
    this.updateCounters();

    this.dispatchEvent('wishlist:cleared');
    this.showToast('Wishlist cleared', 'success');
    return true;
  }

  // Update all wishlist buttons on page
  updateAllButtons() {
    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      const productId = button.dataset.productId;
      if (productId) {
        this.updateButton(button, this.has(productId));
      }
    });

    // Update wishlist indicators
    document.querySelectorAll('[data-wishlist-indicator]').forEach((indicator) => {
      const productId = indicator.dataset.productId;
      if (productId && this.has(productId)) {
        indicator.style.display = 'flex';
      } else {
        indicator.style.display = 'none';
      }
    });
  }

  // Update single button state
  updateButton(button, isInWishlist, animate = false) {
    if (isInWishlist) {
      button.classList.add('is-active');
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('title', 'Remove from wishlist');
    } else {
      button.classList.remove('is-active');
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('title', 'Add to wishlist');
    }

    if (animate) {
      button.classList.add('is-animating');
      setTimeout(() => {
        button.classList.remove('is-animating');
      }, 500);
    }
  }

  // Update all counters on page
  updateCounters() {
    const count = this.count();
    document.querySelectorAll('[data-wishlist-count]').forEach((counter) => {
      const oldCount = counter.textContent;
      counter.textContent = count;
      counter.setAttribute('data-count', count);

      if (count > 0) {
        counter.style.display = '';

        if (oldCount !== count.toString()) {
          counter.style.animation = 'none';
          requestAnimationFrame(() => {
            counter.style.animation = 'wishlistBadgeEntry 0.3s ease-out';
          });
        }
      } else {
        counter.style.display = 'none';
      }
    });

    const wishlistLinks = document.querySelectorAll('[data-wishlist-link]');
    wishlistLinks.forEach((link) => {
      if (count > 0) {
        link.classList.add('has-items');
      } else {
        link.classList.remove('has-items');
      }
    });
  }

  // Attach event listeners
  attachEventListeners() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest('[data-wishlist-button]');
      if (!button) return;

      e.preventDefault();
      e.stopPropagation();

      const productId = button.dataset.productId;
      if (!productId) return;

      this.handleButtonClick(button, productId);
    });
  }

  // Handle wishlist button click
  handleButtonClick(button, productId) {
    if (button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');

    try {
      const productData = this.getProductDataFromButton(button);

      if (productData) {
        this.toggle(productData);
        this.updateButton(button, this.has(productId), true);
      } else {
        const handle = button.dataset.productHandle;
        if (handle) {
          this.fetchProduct(handle).then((product) => {
            if (product) {
              this.toggle(product);
              this.updateButton(button, this.has(productId), true);
            }
          });
        }
      }
    } catch (error) {
      console.error('[Wishlist] Error:', error);
      this.showToast('Error updating wishlist', 'error');
    } finally {
      button.classList.remove('is-loading');
    }
  }

  // Get product data from button attributes
  getProductDataFromButton(button) {
    const productId = button.dataset.productId;
    if (!productId) return null;

    return {
      id: productId,
      handle: button.dataset.productHandle || '',
      title: button.dataset.productTitle || '',
      vendor: button.dataset.productVendor || '',
      price: parseInt(button.dataset.productPrice) || 0,
      compareAtPrice: button.dataset.productComparePrice ? parseInt(button.dataset.productComparePrice) : null,
      image: button.dataset.productImage || '',
      url: button.dataset.productUrl || '',
      available: button.dataset.productAvailable !== 'false',
    };
  }

  // Fetch product data from Shopify API
  async fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Product not found');

      const product = await response.json();

      return {
        id: String(product.id),
        handle: product.handle,
        title: product.title,
        vendor: product.vendor || '',
        price: product.price,
        compareAtPrice: product.compare_at_price || null,
        image: product.featured_image || '',
        url: `/products/${product.handle}`,
        available: product.available,
      };
    } catch (error) {
      return null;
    }
  }

  // Dispatch custom event
  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail: detail,
      bubbles: true,
      cancelable: true,
    });
    document.dispatchEvent(event);
  }

  // Show toast notification
  showToast(message, type = 'success') {
    if (window.showToast && typeof window.showToast === 'function') {
      window.showToast(message, type);
      return;
    }

    const existingToast = document.querySelector('.wishlist-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = `wishlist-toast wishlist-toast--${type}`;

    const icon =
      type === 'success'
        ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>`
        : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`;

    toast.innerHTML = `
      <div class="wishlist-toast__icon">${icon}</div>
      <span>${message}</span>
    `;

    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add('show');
    });

    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 400);
    }, 3000);
  }
}

// Initialize wishlist when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
  });
} else {
  window.wishlistManager = new WishlistManager();
}

// Export for modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = WishlistManager;
}
